import { Link, useLocation } from "@tanstack/react-router";
import {
  CalendarDays,
  ChevronRight,
  FileText,
  HelpCircle,
  Layers,
  MessageSquare,
  Network,
  Workflow,
  type LucideIcon,
} from "lucide-react";

type StageKey =
  | "schedule"
  | "meetings"
  | "questions"
  | "products"
  | "architecture"
  | "candidates"
  | "memo";

interface Stage {
  key: StageKey;
  label: string;
  icon: LucideIcon;
  to: string;
}

const STAGES: readonly Stage[] = [
  { key: "schedule", label: "Schedule", icon: CalendarDays, to: "/timeline-v3" },
  { key: "meetings", label: "Meetings", icon: MessageSquare, to: "/meetings" },
  { key: "questions", label: "Questions", icon: HelpCircle, to: "/open-questions" },
  { key: "products", label: "Products", icon: Layers, to: "/products" },
  { key: "architecture", label: "Architecture", icon: Workflow, to: "/architecture/map" },
  { key: "candidates", label: "Candidates", icon: Network, to: "/candidates" },
  { key: "memo", label: "Memo", icon: FileText, to: "/candidates/compare-summary" },
] as const;

function stageForPath(pathname: string): StageKey | null {
  // Most specific first — `/candidates/compare*` is the memo, not the candidates listing.
  if (pathname.startsWith("/candidates/compare")) return "memo";
  if (pathname.startsWith("/candidates")) return "candidates";
  if (pathname.startsWith("/architecture")) return "architecture";
  if (
    pathname.startsWith("/products") ||
    pathname.startsWith("/evaluation-criteria") ||
    pathname.startsWith("/coverage-matrix") ||
    pathname.startsWith("/roadmaps")
  ) {
    return "products";
  }
  if (
    pathname.startsWith("/open-questions") ||
    pathname.startsWith("/risks") ||
    pathname.startsWith("/decisions") ||
    pathname.startsWith("/tasks")
  ) {
    return "questions";
  }
  if (
    pathname.startsWith("/meetings") ||
    pathname.startsWith("/discoveries") ||
    pathname.startsWith("/stakeholders")
  ) {
    return "meetings";
  }
  if (pathname.startsWith("/timeline")) return "schedule";
  return null;
}

export function PipelineRibbon() {
  const location = useLocation();
  const active = stageForPath(location.pathname);

  return (
    <div
      data-slot="pipeline-ribbon"
      className="bg-background/95 border-border/60 sticky top-0 z-30 border-b backdrop-blur"
    >
      <div className="mx-auto flex h-12 max-w-7xl items-center gap-1 px-4 sm:px-6 lg:px-8">
        <span className="font-data text-foreground/40 mr-3 hidden text-[10px] tracking-[0.18em] uppercase sm:inline">
          Assessment pipeline
        </span>
        <ol className="flex flex-1 items-center gap-0.5">
          {STAGES.map((stage, idx) => {
            const isActive = stage.key === active;
            const Icon = stage.icon;
            const isLast = idx === STAGES.length - 1;
            return (
              <li key={stage.key} className="flex items-center">
                <Link
                  to={stage.to}
                  data-active={isActive ? "true" : undefined}
                  className={[
                    "group inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium transition-all",
                    isActive
                      ? "bg-[oklch(0.72_0.17_190_/_0.10)] text-[oklch(0.45_0.17_190)] ring-1 ring-[oklch(0.72_0.17_190_/_0.35)]"
                      : "text-foreground/50 hover:bg-foreground/[0.04] hover:text-foreground/80",
                  ].join(" ")}
                >
                  <Icon
                    className={[
                      "size-3.5 transition-colors",
                      isActive ? "text-[oklch(0.55_0.17_190)]" : "text-foreground/40",
                    ].join(" ")}
                    strokeWidth={isActive ? 2.5 : 1.75}
                  />
                  <span className="font-data tracking-[0.04em]">{stage.label}</span>
                  {isActive ? (
                    <span
                      className="ml-0.5 size-1.5 rounded-full bg-[oklch(0.55_0.17_190)]"
                      aria-hidden
                    />
                  ) : null}
                </Link>
                {!isLast ? (
                  <ChevronRight className="text-foreground/20 mx-0.5 size-3 shrink-0" aria-hidden />
                ) : null}
              </li>
            );
          })}
        </ol>
      </div>
    </div>
  );
}
